import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Leaf, Award, Shield, Recycle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const certifications = [
  { icon: Leaf, label: 'LEED Certified', description: 'Contributes to LEED credits for sustainable building' },
  { icon: Award, label: 'GREENGUARD', description: 'Low chemical emissions for healthy indoor air quality' },
  { icon: Shield, label: 'ISO Certified', description: 'Meets international quality management standards' },
  { icon: Recycle, label: 'Recycled Content', description: 'Made with post-consumer recycled materials' }
];

export const SustainabilitySection = () => {
  return (
    <section className="section-padding bg-background">
      <div className="container-portico">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-secondary font-semibold mb-3 tracking-wide text-sm">
              ENVIRONMENTAL RESPONSIBILITY
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
              Durable Flooring, Responsible Future
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              We believe high performance and environmental responsibility go hand in hand. 
              Our products are engineered with sustainability in mind—from recycled content 
              to low-VOC adhesives and LEED-contributing materials.
            </p>
            <p className="text-muted-foreground mb-8">
              Choosing Portico means choosing flooring that's better for your space 
              <em> and </em> the planet. Our commitment to sustainability extends from 
              manufacturing to installation and beyond.
            </p>

            {/* Certifications */}
            <TooltipProvider>
              <div className="flex flex-wrap gap-4 mb-8">
                {certifications.map((cert, index) => (
                  <Tooltip key={cert.label}>
                    <TooltipTrigger asChild>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="flex items-center gap-2 bg-muted px-4 py-2 rounded-full cursor-help hover:bg-muted/80 transition-colors"
                      >
                        <cert.icon className="w-5 h-5 text-secondary" />
                        <span className="text-sm font-medium">{cert.label}</span>
                      </motion.div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{cert.description}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>

            {/* Certification Logos */}
            <div className="flex flex-wrap items-center gap-8 mb-8 py-4">
              <img 
                src="/Assets/logos/certifications/UL-Greenguard_GOLD.svg" 
                alt="UL Greenguard Gold Certified" 
                className="h-16"
              />
              <img 
                src="/Assets/logos/certifications/FloorScore_SCS.svg" 
                alt="FloorScore SCS Certified" 
                className="h-16"
              />
              <img 
                src="/Assets/logos/certifications/IAC-eurofins.svg" 
                alt="IAC Eurofins Certified" 
                className="h-16"
              />
              <img 
                src="/Assets/logos/certifications/anab-accredited-logo.svg" 
                alt="ANAB Accredited" 
                className="h-16"
              />
            </div>

            <Link to="/solutions">
              <Button variant="default" className="font-semibold group">
                Explore Our Solutions
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/Assets/images/01_Homepage/sustainability-flooring.jpg"
                alt="Sustainable commercial flooring"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SustainabilitySection;
